import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { ArrowLeft, Loader2, Send, User, Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentComposer, AttachmentTray, AttachmentList } from '../../components/messaging/MessageAttachments';

/**
 * PO-2026-06 — Admin ↔ Talent Direct Message conversation.
 *
 * Route: /admin/messages/dm/:talentId
 * Auto opens (or creates) the ONE canonical direct conversation with that
 * talent and lands the admin INSIDE it — no channel list, no booking needed.
 * Optional ?origin_shift_id= records the gig it was opened from as context.
 */
const AdminDirectMessage = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const originShiftId = params.get('origin_shift_id') || undefined;

  const [threadId, setThreadId] = useState(null);
  const [talent, setTalent] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const bottomRef = useRef(null);

  const loadThread = useCallback(async (tid) => {
    const r = await api.get(`/admin/messages/dm/thread/${tid}`);
    setTalent(r.data.talent);
    setOrigin(r.data.origin_label);
    setMessages(r.data.messages || []);
    // Opening the thread marked the talent's messages read — refresh the badge.
    window.dispatchEvent(new Event('admin-dm-updated'));
  }, []);

  const boot = useCallback(async () => {
    setLoading(true);
    try {
      const open = await api.post('/admin/messages/dm/open', {
        talent_id: talentId,
        origin_shift_id: originShiftId,
      });
      setThreadId(open.data.thread_id);
      setTalent(open.data.talent);
      setOrigin(open.data.origin_label);
      await loadThread(open.data.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open conversation');
    } finally {
      setLoading(false);
    }
  }, [talentId, originShiftId, loadThread]);

  useEffect(() => { boot(); }, [boot]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    const content = draft.trim();
    if ((!content && pending.length === 0) || sending || !threadId) return;
    setSending(true);
    try {
      await api.post(`/admin/messages/dm/thread/${threadId}/send`, { content, attachments: pending });
      setDraft('');
      setPending([]);
      await loadThread(threadId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const initials = (talent?.name || 'T').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="admin-dm-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/messages')} data-testid="admin-dm-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Avatar className="w-9 h-9">
            {talent?.photo ? <AvatarImage src={talent.photo} alt={talent?.name} /> : null}
            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-slate-900 truncate" data-testid="admin-dm-talent-name">
              {talent?.name || 'Talent'}
            </h1>
            <p className="text-[11px] text-slate-500 truncate">Direct message</p>
          </div>
          {talent?.id && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/talent/${talent.id}`)} data-testid="admin-dm-profile-btn">
              <User className="w-4 h-4 mr-1" /> Profile
            </Button>
          )}
        </div>
        {origin && (
          <div className="max-w-3xl mx-auto px-4 pb-2">
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1" data-testid="admin-dm-origin-banner">
              <Info className="w-3 h-3" /> {origin} · context only
            </div>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto" data-testid="admin-dm-messages">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-2">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-cyan-600 inline" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500" data-testid="admin-dm-empty">
              No messages yet. Say hello 👋
            </div>
          ) : (
            messages.map((m) => {
              const isAi = m.is_ai;
              const bubble = m.is_admin
                ? 'bg-cyan-600 text-white rounded-br-sm'
                : isAi
                  ? 'bg-indigo-50 border border-indigo-200 text-indigo-950 rounded-bl-sm'
                  : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm';
              return (
                <div key={m.id} className={`flex ${m.is_admin ? 'justify-end' : 'justify-start'}`} data-testid={`admin-dm-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${bubble}`}>
                    {isAi && (
                      <div className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700 mb-1" data-testid={`admin-dm-ai-label-${m.id}`}>
                        <Sparkles className="w-3 h-3" /> Evie · Virtual Assistant
                      </div>
                    )}
                    {m.content && <div className="whitespace-pre-wrap break-words">{m.content}</div>}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mt-1.5"><AttachmentList attachments={m.attachments} align={m.is_admin ? 'right' : 'left'} /></div>
                    )}
                    <div className={`text-[10px] mt-1 ${m.is_admin ? 'text-cyan-100' : (isAi ? 'text-indigo-500' : 'text-slate-500')}`}>
                      {m.is_admin ? 'You (Admin)' : (isAi ? 'Evie · Virtual Assistant' : (talent?.name || 'Talent'))}
                      {m.created_at ? ` · ${new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <footer className="bg-white border-t sticky bottom-0">
        <div className="max-w-3xl mx-auto px-3 py-3">
          <AttachmentTray pending={pending} setPending={setPending} />
          <div className="flex items-end gap-2">
            <AttachmentComposer pending={pending} setPending={setPending} disabled={sending || loading || !threadId} />
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Message ${talent?.name || 'talent'}…`}
              rows={1}
              className="min-h-[40px] resize-none"
              data-testid="admin-dm-input"
            />
            <Button onClick={send} disabled={sending || loading || !threadId || (!draft.trim() && pending.length === 0)} className="bg-cyan-600 hover:bg-cyan-700 text-white shrink-0" data-testid="admin-dm-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminDirectMessage;
